'use client';

import { AppImageClient } from '@components/appImage';
import { Typography } from '@components/typography';
import { sendAnalyticsEvent } from '@lib/analytics/analytics';
import { cn } from '@lib/utils/cn';
import { useMemo } from 'react';

import { AnimationType, PageTitleBlockProps } from './types';
import { Button } from '../../button';
import { ScrollButton } from '../../scrollButton';

import { useIsMobile, useUtmHref } from '@/shared/hooks';

export const PageTitleBlock = ({
  title,
  titleId,
  titleMaxWidth,
  description,
  descriptionId,
  descriptionMaxWidth,
  buttonsElement,
  mainButton,
  secondaryButton,
  imgSrc,
  imageData,
  imageSize,
  imgSrcAlt,
  imageDimensions,
  animationType = 'float',
  contentDimensions,
  centerImage = false,
}: PageTitleBlockProps) => {
  const isMobile = useIsMobile();
  const animationOffset = useMemo(
    () => getAnimationOffset(animationType),
    [animationType],
  );
  const blockStyles = useMemo(
    () =>
      getBlockStyles({
        imageDimensions,
        contentDimensions,
        isMobile,
      }),
    [contentDimensions, imageDimensions, isMobile],
  );

  const mainButtonHref = useUtmHref(mainButton?.href);
  const secondaryButtonHref = useUtmHref(secondaryButton?.href);

  const mainButtonElement = useMemo(() => {
    if (!mainButton) {
      return null;
    }

    const handleButtonClick = () => {
      if (mainButton.analyticsEvent) {
        sendAnalyticsEvent(mainButton.analyticsEvent);
      }
    };

    if (mainButtonHref) {
      return (
        <Button
          className={cn(
            'flex items-center justify-center px-6 py-4 max-sm:w-full',
            mainButton.className,
          )}
          variant={mainButton.variant ?? 'primary'}
          disabled={false}
          role="button"
          href={mainButtonHref}
          target={mainButton.target}
          onClick={handleButtonClick}
        >
          {mainButton.label}
        </Button>
      );
    }

    return (
      <ScrollButton
        target="form"
        className={cn(
          'flex items-center justify-center px-6 py-4 max-sm:w-full',
          mainButton.className,
        )}
        variant={mainButton.variant ?? 'primary'}
      >
        {mainButton.label}
      </ScrollButton>
    );
  }, [mainButton, mainButtonHref]);

  const secondaryButtonElement = useMemo(() => {
    if (!secondaryButton) {
      return null;
    }

    const handleButtonClick = () => {
      if (secondaryButton?.analyticsEvent) {
        sendAnalyticsEvent(secondaryButton?.analyticsEvent);
      }
    };

    if (secondaryButtonHref) {
      return (
        <Button
          className={cn(
            'flex items-center justify-center px-4 py-4 max-sm:w-full',
            secondaryButton.className,
          )}
          variant={secondaryButton.variant ?? 'secondary'}
          role="button"
          href={secondaryButtonHref}
          onClick={handleButtonClick}
          target={secondaryButton.target}
        >
          {secondaryButton.label}
        </Button>
      );
    }

    return (
      <ScrollButton
        target="form"
        className={cn(
          'flex items-center justify-center px-4 py-4 max-sm:w-full',
          secondaryButton.className,
        )}
        variant={secondaryButton.variant ?? 'secondary'}
      >
        {secondaryButton.label}
      </ScrollButton>
    );
  }, [secondaryButton, secondaryButtonHref]);

  const imgStyle = useMemo(
    () => ({
      ...getImageStyle(imageDimensions, isMobile),
      animation: getAnimation(animationType),
    }),
    [animationType, imageDimensions, isMobile],
  );

  const imageWrapperStyleForWeb = {
    ...blockStyles.image,
    paddingTop: animationOffset.top,
    paddingBottom: animationOffset.bottom,
  };

  const imageWrapperStyleForMobile = {
    paddingTop: animationOffset.top,
    paddingBottom: animationOffset.bottom,
  };

  return (
    <>
      {/** WEB */}
      <section className={cn('m-auto max-w-[1200px] px-4 pt-11 max-lg:hidden')}>
        <div className="relative flex h-full gap-8">
          <div style={blockStyles.content} className="flex items-center">
            <div className={cn('flex min-w-0 flex-col gap-10 max-md:gap-8')}>
              <div className="flex flex-col gap-5 max-md:gap-4">
                {/** Заголовок */}
                <h1
                  className="typo-heading-xl"
                  id={titleId}
                  style={{ maxWidth: titleMaxWidth }}
                >
                  {title}
                </h1>

                {/** Описание */}
                {description && (
                  <Typography
                    variant="text-m"
                    id={descriptionId}
                    style={{ maxWidth: descriptionMaxWidth }}
                    className={cn(
                      '[&>a]:cursor-pointer [&>a]:underline [&>a]:underline-offset-[3px]',
                    )}
                  >
                    {description}
                  </Typography>
                )}
              </div>

              {/** Блок с кнопками */}
              {buttonsElement ?? (
                <div className={cn('flex w-full flex-wrap items-center gap-4')}>
                  {mainButtonElement}
                  {secondaryButtonElement}
                </div>
              )}
            </div>
          </div>

          {/**
           * Картинка
           *
           * Здесь мы создаем обертку с адаптивными стилями, а внутри кладем
           * обертку для разметки картинки с абсолютным позиционированием,
           * потому что в дизайн-системе блок строится от текстового
           * наполнения, поэтому картинка катается в своей коробочке
           */}
          <div style={blockStyles.image}>
            <div
              style={imageWrapperStyleForWeb}
              className="absolute top-[50%] right-0 min-w-0 -translate-y-[50%]"
            >
              {imageData && imageSize ? (
                <AppImageClient
                  {...imageData}
                  width={imageSize.width}
                  height={imageSize.height}
                  className={cn(
                    'block max-w-full object-contain',
                    centerImage && 'mx-auto',
                  )}
                  alt={imgSrcAlt ?? 'main_title_block_image'}
                  style={imgStyle}
                  priority
                />
              ) : (
                <img
                  className={cn(
                    'block max-w-full object-contain',
                    centerImage && 'mx-auto',
                  )}
                  src={imgSrc}
                  alt={imgSrcAlt ?? 'main_title_block_image'}
                  style={imgStyle}
                  decoding="async"
                  loading="eager"
                  fetchPriority="high"
                />
              )}
            </div>
          </div>
        </div>

        {/** Стили для анимации картинки */}
        <style jsx global>{`
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(25px);
            }
            100% {
              transform: translateY(0px);
            }
          }
        `}</style>
      </section>

      {/** MOBILE */}
      <section className={cn('m-auto max-w-[1200px] px-4 pt-20 lg:hidden')}>
        <div className="flex h-full flex-col gap-8">
          <div style={imageWrapperStyleForMobile}>
            {imageData && imageSize ? (
              <AppImageClient
                {...imageData}
                width={imageSize.width}
                height={imageSize.height}
                className={cn('block max-w-full object-contain')}
                alt={imgSrcAlt ?? 'main_title_block_image'}
                style={imgStyle}
                priority
              />
            ) : (
              <img
                className={cn('block max-w-full object-contain')}
                src={imgSrc}
                alt={imgSrcAlt ?? 'main_title_block_image'}
                style={imgStyle}
                decoding="async"
                loading="eager"
                fetchPriority="high"
              />
            )}
          </div>

          <div className="flex min-w-0 flex-col justify-between gap-8">
            <div className="flex flex-col gap-5">
              {/** Заголовок */}
              <h1
                className="typo-heading-xl"
                id={titleId}
                style={{ maxWidth: titleMaxWidth }}
              >
                {title}
              </h1>

              {/** Описание */}
              {description && (
                <Typography
                  variant="text-m"
                  id={descriptionId}
                  style={{ maxWidth: descriptionMaxWidth }}
                  className="[&>a]:cursor-pointer [&>a]:underline [&>a]:underline-offset-[3px]"
                >
                  {description}
                </Typography>
              )}
            </div>

            {/** Блок с кнопками */}
            {buttonsElement ?? (
              <div className="flex w-full flex-wrap items-center gap-4">
                {mainButtonElement}
                {secondaryButtonElement}
              </div>
            )}
          </div>
        </div>

        {/** Стили для анимации картинки */}
        <style jsx global>{`
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(25px);
            }
            100% {
              transform: translateY(0px);
            }
          }
        `}</style>
      </section>
    </>
  );
};

const getAnimation = (type?: AnimationType): string => {
  switch (type) {
    case 'float':
      return 'float 4s linear infinite';
    case 'none':
    default:
      return 'none';
  }
};

// Определена как максимальная ширина 1200 - отступы 32 - gap 24
const SECTION_CONTENT_WIDTH = 1200 - 32 - 24;
const DEFAULT_CONTENT_HEIGHT = 400;

const getBlockStyles = ({
  imageDimensions,
  contentDimensions,
  isMobile,
}: {
  imageDimensions: PageTitleBlockProps['imageDimensions'];
  contentDimensions?: PageTitleBlockProps['contentDimensions'];
  isMobile: boolean;
}): {
  content: React.CSSProperties;
  image: React.CSSProperties;
} => {
  const minHeight = contentDimensions?.height ?? DEFAULT_CONTENT_HEIGHT;

  if (isMobile) {
    return {
      content: {
        minHeight,
      },
      image: {},
    };
  }

  const contentWidth = contentDimensions?.width;

  if (typeof contentWidth === 'number') {
    const contentWidthPercent = Math.min(
      (contentWidth / SECTION_CONTENT_WIDTH) * 100,
      100,
    );
    const imageWidthPercent = Math.max(100 - contentWidthPercent, 0);

    return {
      content: {
        minHeight,
        flex: `0 1 ${contentWidthPercent}%`,
        width: `${contentWidthPercent}%`,
        maxWidth: `${contentWidthPercent}%`,
      },
      image: {
        flex: `0 1 ${imageWidthPercent}%`,
        width: `${imageWidthPercent}%`,
        maxWidth: `${imageWidthPercent}%`,
      },
    };
  }

  const imageWidth = imageDimensions?.width;

  if (typeof imageWidth === 'number') {
    const imageWidthPercent = Math.min(
      (imageWidth / SECTION_CONTENT_WIDTH) * 100,
      100,
    );
    const contentWidthPercent = Math.max(100 - imageWidthPercent, 0);

    return {
      content: {
        minHeight,
        flex: `1 1 ${contentWidthPercent}%`,
        width: `${contentWidthPercent}%`,
        maxWidth: contentDimensions?.width ?? `${contentWidthPercent}%`,
      },
      image: {
        flex: `0 1 ${imageWidthPercent}%`,
        width: `${imageWidthPercent}%`,
        maxWidth: `${imageWidthPercent}%`,
      },
    };
  }

  return {
    content: {
      minHeight,
      flex: '1 1 0%',
      maxWidth: contentDimensions?.width,
    },
    image: {
      width: imageDimensions?.width,
      maxWidth: imageDimensions?.width,
      flexShrink: 1,
    },
  };
};

const getImageStyle = (
  imageDimensions: PageTitleBlockProps['imageDimensions'],
  isMobile: boolean,
): React.CSSProperties => {
  if (isMobile) {
    return {
      width: '100%',
      height: 'auto',
    };
  }

  if (imageDimensions?.width !== undefined) {
    return {
      width:
        typeof imageDimensions.width === 'number'
          ? imageDimensions.width
          : '100%',
      height: imageDimensions.height ? imageDimensions.height : 'auto',
    };
  }

  if (imageDimensions?.height !== undefined) {
    return {
      width: 'auto',
      height: imageDimensions.height,
      maxWidth: '100%',
    };
  }

  return {
    width: '100%',
    height: 'auto',
  };
};

const getAnimationOffset = (
  type?: AnimationType,
): { top: number; bottom: number } => {
  switch (type) {
    case 'float':
      return { top: 0, bottom: 25 };
    case 'none':
    default:
      return { top: 0, bottom: 0 };
  }
};
